/**
 * AnalysisV2Layout — the redesigned v2 audit shell.
 *
 * One horizontal nav with four click-based dropdowns:
 *   [Profile Audit] [Profile Rewrite ▾] [Create Content ▾] [Grow Presence ▾]
 *
 * Dropdowns show checkmarks for done items (Rewrite only, from
 * localStorage), a filled dot for the current selection, and a lock icon
 * for gated Create Content sub-pages. Profile Audit is a leaf item.
 *
 * The old horizontal tab strip and the "Generate Posts" button are
 * removed — Create Content now covers generation entry.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Lock,
  FileText,
  Pen,
  MessagesSquare,
  TrendingUp,
  Download,
  Loader2,
  FileDown,
  Wand2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  OverviewTab,
  DiscoverabilityTab,
  ProofTab,
  PlanTab,
  SevenDayPlanTab,
  PresenceTab,
} from '../../components/results/FullAuditReport';
import RewriteWorkflow, { REWRITE_SECTIONS } from './RewriteWorkflow';
import PostsResults from '../../components/results/PostsResults';
import PovPostsTab from '../../components/results/PovPostsTab';
import PromptPostsTab from '../../components/results/PromptPostsTab';
import PeerBenchmarkTab from '../../components/results/PeerBenchmarkTab';
import TopicsToWriteTab from '../../components/results/TopicsToWriteTab';
import ResumeMakeoverTab from '../../components/results/ResumeMakeoverTab';
import { readProgress } from '../../lib/rewriteProgress';
import { useAuditViewTracker } from '../../lib/useAuditViewTracker';

// ---------------- nav definition ----------------
// Each menu → its children pages. Nav ids are stable and used both as
// dropdown item keys and as the top-level page routing key inside this
// component.
const NAV = [
  {
    id: 'audit',
    label: 'Profile Audit',
    icon: FileText,
    items: [
      { key: 'audit.overview', label: 'Overview' },
      { key: 'audit.discoverability', label: 'Discoverability' },
      { key: 'audit.proof', label: 'Proof & Credibility' },
    ],
  },
  {
    id: 'rewrite',
    label: 'Profile Rewrite',
    icon: Pen,
    items: REWRITE_SECTIONS.map((s) => ({ key: `rewrite.${s.key}`, label: s.label })),
  },
  {
    id: 'content',
    label: 'Create Content',
    icon: MessagesSquare,
    items: [
      { key: 'content.researched', label: 'Researched Posts' },
      { key: 'content.pov', label: 'POV Posts' },
      { key: 'content.prompt', label: 'Prompt Posts' },
      { key: 'content.topics', label: 'Topics to write on' },
      { key: 'content.peer_benchmark', label: 'Peer benchmark' },
    ],
  },
  {
    id: 'presence',
    label: 'Grow Presence',
    icon: TrendingUp,
    items: [
      { key: 'presence.keywords', label: 'Keyword Optimisation' },
      { key: 'presence.themes', label: 'Presence Themes' },
      { key: 'presence.seven', label: '7-Day Plan' },
      { key: 'presence.thirty', label: '30-Day Plan' },
    ],
  },
  // Resume Makeover — top-level leaf so it's one click from anywhere.
  {
    id: 'resume',
    label: 'Resume Makeover',
    icon: Wand2,
    items: [
      { key: 'resume.makeover', label: 'Resume Makeover' },
    ],
    leaf: true,
    leafKey: 'resume.makeover',
    badge: 'New',
  },
];

// ---------------- one click-dropdown ----------------
function NavDropdown({ menu, activeKey, onSelect, done, gatedKeys = new Set() }) {
  const [open, setOpen] = useState(false);
  const isActiveMenu = activeKey.startsWith(menu.id);
  const currentChild = menu.items.find((i) => i.key === activeKey);
  const Icon = menu.icon;

  // Leaf menus (e.g. Resume Makeover) render as a direct-click pill —
  // no dropdown — so the user gets there in one click instead of two.
  if (menu.leaf) {
    const isLocked = gatedKeys.has(menu.leafKey);
    return (
      <button
        type="button"
        onClick={() => { if (!isLocked) onSelect(menu.leafKey); }}
        className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium transition-all ${
          isActiveMenu
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
        }`}
        data-testid={`v2nav-menu-${menu.id}`}
        disabled={isLocked}
      >
        <Icon size={14} className={isActiveMenu ? '' : 'opacity-70'} />
        <span>{menu.label}</span>
        {menu.badge && !isActiveMenu && (
          <span className="ml-0.5 inline-flex items-center rounded-full bg-amber-500 text-white text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
            {menu.badge}
          </span>
        )}
        {isLocked && <Lock size={11} className="opacity-70" />}
      </button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium transition-all ${
            isActiveMenu
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
          }`}
          data-testid={`v2nav-menu-${menu.id}`}
        >
          <Icon size={14} className={isActiveMenu ? '' : 'opacity-70'} />
          <span>{menu.label}</span>
          {isActiveMenu && currentChild && (
            <span className="hidden sm:inline text-[11.5px] opacity-90">· {currentChild.label}</span>
          )}
          <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid={`v2nav-menu-${menu.id}-content`}>
        {menu.items.map((child) => {
          const isDone = !!done[child.key];
          const isActive = child.key === activeKey;
          const isLocked = gatedKeys.has(child.key);
          return (
            <DropdownMenuItem
              key={child.key}
              disabled={isLocked}
              onSelect={(e) => {
                if (isLocked) { e.preventDefault(); return; }
                onSelect(child.key);
                setOpen(false);
              }}
              className={`flex items-center gap-2 text-[13px] ${isActive ? 'bg-primary/10 text-foreground' : ''}`}
              data-testid={`v2nav-item-${child.key}`}
            >
              <span className="inline-flex h-4 w-4 items-center justify-center shrink-0">
                {isDone ? (
                  <Check size={12} className="text-emerald-600" />
                ) : isActive ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-transparent" />
                )}
              </span>
              <span className={isActive ? 'font-medium' : ''}>{child.label}</span>
              {isLocked && <Lock size={11} className="ml-auto text-muted-foreground" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------- Mobile-only single selector ----------------
// Collapses the 4-dropdown desktop nav into one "Section: X / Y ▾"
// pill on viewports narrower than 640px. Groups are rendered as a
// flat list with a small header per menu so the surface stays
// scannable at ~90 items or fewer.
function MobileNavSelect({ activeKey, onSelect, done, gated }) {
  const [open, setOpen] = useState(false);
  const currentMenu = NAV.find((m) => activeKey.startsWith(m.id));
  const currentItem = currentMenu?.items.find((i) => i.key === activeKey);
  const label = currentMenu ? `${currentMenu.label}` : 'Choose section';
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full inline-flex items-center justify-between gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium shadow-sm"
          data-testid="v2nav-mobile-trigger"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="truncate">{label}</span>
            {currentItem && <span className="opacity-85 text-[11.5px] truncate">/ {currentItem.label}</span>}
          </span>
          <ChevronDown size={13} className={`transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-[calc(100vw-2rem)] max-w-[380px] max-h-[70vh] overflow-y-auto"
      >
        {NAV.map((menu) => (
          <div key={menu.id} className="py-1">
            <p className="px-2 pt-1 pb-0.5 text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1.5">
              <menu.icon size={11} /> {menu.label}
            </p>
            {menu.items.map((child) => {
              const isDone = !!done[child.key];
              const isActive = child.key === activeKey;
              const isLocked = gated.has(child.key);
              return (
                <DropdownMenuItem
                  key={child.key}
                  disabled={isLocked}
                  onSelect={(e) => { if (isLocked) { e.preventDefault(); return; } onSelect(child.key); setOpen(false); }}
                  className={`flex items-center gap-2 text-[13px] pl-4 ${isActive ? 'bg-primary/10 text-foreground' : ''}`}
                  data-testid={`v2nav-mobile-item-${child.key}`}
                >
                  <span className="inline-flex h-4 w-4 items-center justify-center shrink-0">
                    {isDone ? <Check size={12} className="text-emerald-600" /> : isActive ? <span className="h-1.5 w-1.5 rounded-full bg-primary" /> : <span className="h-1.5 w-1.5 rounded-full bg-transparent" />}
                  </span>
                  <span className={isActive ? 'font-medium' : ''}>{child.label}</span>
                  {isLocked && <Lock size={11} className="ml-auto text-muted-foreground" />}
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------- Root component ----------------
export default function AnalysisV2Layout({
  data,
  me,
  contentGated,
  peerUnlocked = false,
  onUpgrade,
  regenerating,
  onRegenerateRequested,
  onRetryLast,
  onDownload,
  downloading,
}) {
  // Local audit state so a rewrite-tone regeneration refreshes the whole
  // audit tab in place without needing a page reload / re-fetch. We
  // seed from the freshest data prop but also re-sync when the parent
  // hands us a newer document (e.g. after posts regeneration).
  const [audit, setAudit] = useState(data.full_audit_v2 || {});
  useEffect(() => { setAudit(data.full_audit_v2 || {}); }, [data.full_audit_v2]);
  const analysisId = data.id;
  const navigate = useNavigate();

  const [toneRegenerating, setToneRegenerating] = useState(false);
  const [lensRegenerating, setLensRegenerating] = useState(false);
  // Track which single section (headline / about) is currently
  // regenerating so the Rewrite tab can show a scoped spinner.
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [regeneratingDimKey, setRegeneratingDimKey] = useState(null);

  const handleRegenerateDimension = async (dimensionKey) => {
    if (!analysisId || !dimensionKey || regeneratingDimKey) return;
    setRegeneratingDimKey(dimensionKey);
    const toastId = toast.loading('Rewriting this dimension… ~15-30s.');
    try {
      const { linkedge } = await import('../../lib/api');
      const res = await linkedge.regenerateDimension(analysisId, dimensionKey);
      if (res?.dimension) {
        setAudit((prev) => ({
          ...prev,
          dimensions: (prev.dimensions || []).map((d) =>
            d.key === dimensionKey ? { ...d, ...res.dimension } : d
          ),
        }));
        toast.success('Dimension rewritten.', { id: toastId });
      } else {
        toast.error('Regenerate returned no dimension.', { id: toastId });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Regenerate failed') : (detail || 'Regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setRegeneratingDimKey(null);
    }
  };
  const handleRegenerateTone = async (slider) => {
    if (toneRegenerating || !analysisId || !slider || slider === audit.languageSlider) return;
    setToneRegenerating(true);
    const toastId = toast.loading('Re-writing your profile in the new tone… this can take 2-4 minutes.');
    try {
      const { linkedge } = await import('../../lib/api');
      // Kick off the background job. Endpoint returns 202 immediately so
      // Cloudflare's ~100s edge timeout doesn't kill the request.
      await linkedge.regenerateTone(analysisId, slider);
      // Poll every 6 seconds for up to ~6 minutes.
      const started = Date.now();
      const maxMs = 6 * 60 * 1000;
      let done = false;
      let lastState = 'running';
      while (Date.now() - started < maxMs) {
        await new Promise((r) => setTimeout(r, 6000));
        const status = await linkedge.regenerateToneStatus(analysisId);
        lastState = status?.state;
        if (status?.state === 'done' && status?.full_audit_v2?.languageSlider === slider) {
          setAudit(status.full_audit_v2);
          const nice = slider === 'high_storytelling' ? 'High-storytelling' : slider.charAt(0).toUpperCase() + slider.slice(1);
          toast.success(`Rewrite tone changed to ${nice}.`, { id: toastId });
          done = true;
          break;
        }
        if (status?.state === 'failed') {
          toast.error(status?.error || 'Regenerate failed. Please try again.', { id: toastId });
          done = true;
          break;
        }
      }
      if (!done) {
        toast.error(`Still running (${lastState}) — refresh in a minute to see the new tone.`, { id: toastId });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Regenerate failed') : (detail || 'Regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setToneRegenerating(false);
    }
  };

  const handleRegenerateLens = async (lens) => {
    if (lensRegenerating || !analysisId || !lens) return;
    if (lens === audit.recruiterLens) return;
    setLensRegenerating(true);
    const nice = lens === 'trajectory' ? 'Trajectory' : 'Risk';
    const toastId = toast.loading(`Re-writing your profile through the ${nice} recruiter lens… ~30-45s.`);
    try {
      const { linkedge } = await import('../../lib/api');
      await linkedge.regenerateLens(analysisId, lens);
      const started = Date.now();
      const maxMs = 5 * 60 * 1000;
      let done = false;
      let lastState = 'running';
      while (Date.now() - started < maxMs) {
        await new Promise((r) => setTimeout(r, 4500));
        const status = await linkedge.regenerateLensStatus(analysisId);
        lastState = status?.state;
        if (status?.state === 'done' && status?.full_audit_v2?.recruiterLens === lens) {
          setAudit(status.full_audit_v2);
          toast.success(`Rewrites now read through the ${nice} recruiter lens.`, { id: toastId });
          done = true;
          break;
        }
        if (status?.state === 'failed') {
          toast.error(status?.error || 'Recruiter-lens regenerate failed. Please try again.', { id: toastId });
          done = true;
          break;
        }
      }
      if (!done) {
        toast.error(`Still running (${lastState}) — refresh in a moment to see the new lens.`, { id: toastId });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Recruiter-lens regenerate failed') : (detail || 'Recruiter-lens regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setLensRegenerating(false);
    }
  };

  const [peerPositionedHeadline, setPeerPositionedHeadline] = useState(null);
  const [peerPositioning, setPeerPositioning] = useState(null);
  const [positioningRegenerating, setPositioningRegenerating] = useState(false);

  const handleRegenerateHeadlineWithPositioning = async () => {
    if (!analysisId || positioningRegenerating) return;
    setPositioningRegenerating(true);
    const toastId = toast.loading('Rewriting your Headline using peer-positioning gap analysis… ~20-30s.');
    try {
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('le_token') || '';
      const headers = { Authorization: `Bearer ${token}` };

      // Two-step orchestration so we stay under the ~60s Cloudflare
      // gateway ceiling on older audits (each LLM call ~30s). New
      // audits already have peer_positioning cached from
      // run_content_prep so the first call becomes a no-op fast path.
      const cachedPositioning = peerPositioning || data?.peer_positioning || null;
      let currentPositioning = cachedPositioning;
      if (!currentPositioning) {
        toast.loading('Step 1 of 2 · analysing your peer cohort…', { id: toastId });
        const { data: prep } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/analyses/${analysisId}/regenerate-peer-positioning`,
          {},
          { headers },
        );
        currentPositioning = prep?.peer_positioning || null;
        if (currentPositioning) {
          setPeerPositioning(currentPositioning);
        }
      }

      toast.loading(
        currentPositioning
          ? 'Rewriting your Headline into the positioning gap…'
          : 'Rewriting your Headline… (positioning will be inferred inline)',
        { id: toastId },
      );
      const { data: resp } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analyze/${analysisId}/headline/regenerate-with-positioning`,
        {},
        { headers },
      );
      if (resp?.headline_peer_positioned) {
        setPeerPositionedHeadline(resp.headline_peer_positioned);
      }
      if (resp?.peer_positioning) {
        setPeerPositioning(resp.peer_positioning);
      }
      toast.success('Peer-positioned Headline is ready — see the panel below.', { id: toastId });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Peer-positioning regen failed') : (detail || 'Peer-positioning regen failed');
      toast.error(msg, { id: toastId });
    } finally {
      setPositioningRegenerating(false);
    }
  };

  const handleRegenerateSection = async (section, { userPrompt } = {}) => {
    if (!analysisId || !section) return;
    if (regeneratingSection) return;
    setRegeneratingSection(section);
    const nice = section === 'headline' ? 'Headline' : 'About';
    const toastId = toast.loading(
      userPrompt ? `Applying your request to ${nice}…` : `Regenerating your ${nice}…`,
    );
    try {
      const payload = { section };
      if (userPrompt) payload.user_prompt = userPrompt;
      const { data } = await (await import('axios')).default.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analyze/${analysisId}/regenerate-section`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('le_token') || ''}` } },
      );
      setAudit((cur) => ({ ...(cur || {}), [section]: data?.[section] || (cur || {})[section] }));
      toast.success(
        userPrompt ? `${nice} updated with your request.` : `${nice} regenerated.`,
        { id: toastId },
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Regenerate failed') : (detail || 'Regenerate failed');
      toast.error(msg, { id: toastId });
    } finally {
      setRegeneratingSection(null);
    }
  };

  // Selected page.
  const [pageKey, setPageKey] = useState('audit.overview');

  // Track per-tab views + dwell time (used by admins to see what a
  // comp-audit recipient actually opened after clicking the magic link).
  useAuditViewTracker(analysisId, pageKey);

  // Rewrite progress (localStorage).
  const [progress, setProgress] = useState(() => readProgress(analysisId));

  // Refresh progress if analysis id changes.
  useEffect(() => { setProgress(readProgress(analysisId)); }, [analysisId]);

  // Map rewrite progress to dropdown "done" ticks for the nav
  // (namespaced with `rewrite.` prefix so it matches item keys).
  const doneMap = useMemo(() => {
    const m = {};
    Object.keys(progress).forEach((k) => { m[`rewrite.${k}`] = progress[k]; });
    return m;
  }, [progress]);

  // Gated Create Content items.
  const gated = useMemo(() => {
    const s = new Set();
    if (contentGated) {
      s.add('content.researched');
      s.add('content.pov');
      s.add('content.prompt');
      s.add('content.topics');
    }
    // Peer benchmarking is Enterprise-only. Admin unlocks it either
    // per-user (`me.is_enterprise`) or per-audit (`data.enterprise_view`),
    // both surfaced via the ``peerUnlocked`` prop from the parent.
    if (!peerUnlocked) {
      s.add('content.peer_benchmark');
    }
    return s;
  }, [contentGated, peerUnlocked]);

  // Resume Makeover is paid-plan only. Non-paid users can still open the
  // tab (so they see the upgrade CTA); the tab itself renders a locked
  // view via ``planLocked`` when the plan isn't gold / one_time / comp.
  // ``me`` here is the billing snapshot (plan_type ∈ {admin, gold, one_time,
  // comp, none, …}). ``can_download`` short-circuits for admin/comp.
  const resumeLocked = useMemo(() => {
    if (me?.can_download) return false;
    const _pt = me?.plan_type || 'none';
    const _st = me?.status || 'inactive';
    if (_pt === 'admin' || _pt === 'editor') return false;
    return !(['gold', 'one_time', 'comp'].includes(_pt) && _st === 'active');
  }, [me]);

  // Rewrite sub-key (headline / about / …) derived from pageKey.
  const rewriteSubKey = pageKey.startsWith('rewrite.') ? pageKey.slice('rewrite.'.length) : 'headline';

  // Content prep is deferred out of the audit pipeline so the audit
  // finishes fast. When the user first visits any Create Content /
  // Grow Presence tab, kick off (or re-kick) the prep task so voice
  // profile + thought leaders + trending topics get generated.
  // Fires at most once per mount + when the prep status transitions
  // away from 'ready'.
  useEffect(() => {
    const needsPrep = pageKey.startsWith('content.') || pageKey.startsWith('presence.');
    const prepStatus = data?.content_prep_status;
    if (!needsPrep || !analysisId) return;
    if (prepStatus === 'ready' || prepStatus === 'running') return;
    // 'pending', 'error', undefined, null → kick it
    let cancelled = false;
    (async () => {
      try {
        const { linkedge } = await import('../../lib/api');
        if (cancelled) return;
        await linkedge.prepareContent(analysisId);
      } catch (e) {
        // Non-fatal — surface via existing content-tab empty states.
        console.warn('prepare-content kickoff failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [pageKey, data?.content_prep_status, analysisId]);

  // Prompt-post pre-fill from a Presence Plan theme → deep-link into
  // Create Content · Prompt Posts with topic + first two actions
  // pre-populated. Consumed by <PromptPostsTab defaults={...} />.
  const [promptDefaults, setPromptDefaults] = useState(null);
  // POV seed theme → deep-link into Create Content · POV Posts. The
  // POV composer auto-opens with a banner reminding the user which
  // theme they should choose a supporting news story for.
  const [povSeedTheme, setPovSeedTheme] = useState(null);
  const handleDraftFromTheme = (theme) => {
    setPromptDefaults({
      topic: (theme?.theme || '').slice(0, 240),
      custom_topics: (theme?.actions || []).slice(0, 3).map((a) => (typeof a === 'string' ? a : a?.action || '')).filter(Boolean),
    });
    setPageKey('content.prompt');
    toast.success('Draft prompt ready — tweak and generate below.');
  };
  const handleDraftPovFromTheme = (theme) => {
    setPovSeedTheme({
      theme: (theme?.theme || '').slice(0, 240),
      why: (theme?.why || '').slice(0, 300),
    });
    setPageKey('content.pov');
    toast.success('POV composer opening — pick a story that supports this theme.');
  };

  // ---------- content rendering ----------
  let content = null;
  if (pageKey === 'audit.overview') content = (
    <OverviewTab
      audit={audit}
      rawData={data}
      onRegenerateTone={handleRegenerateTone}
      regenerating={toneRegenerating}
      onRegenerateDimension={handleRegenerateDimension}
      regeneratingDimKey={regeneratingDimKey}
      onGoToRewrite={() => {
        setPageKey('rewrite.headline');
        setTimeout(() => {
          try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
        }, 30);
      }}
      onChangeGoal={() => navigate('/dashboard')}
    />
  );
  else if (pageKey === 'audit.discoverability') content = <DiscoverabilityTab audit={audit} />;
  else if (pageKey === 'audit.proof') content = <ProofTab audit={audit} />;
  else if (pageKey === 'resume.makeover') content = (
    <ResumeMakeoverTab
      analysisId={analysisId}
      planLocked={resumeLocked}
      onUpgrade={onUpgrade}
    />
  );
  else if (pageKey.startsWith('rewrite.')) content = (
    <RewriteWorkflow
      audit={audit}
      analysisId={analysisId}
      sectionKey={rewriteSubKey}
      progress={progress}
      onSectionChange={(k) => setPageKey(`rewrite.${k}`)}
      onProgressChange={setProgress}
      toneVariants={data.tone_variants || {}}
      lensVariants={data.lens_variants || {}}
      onRegenerateTone={handleRegenerateTone}
      regenerating={toneRegenerating}
      onRegenerateLens={handleRegenerateLens}
      lensRegenerating={lensRegenerating}
      onRegenerateSection={handleRegenerateSection}
      regeneratingSection={regeneratingSection}
      onRegenerateHeadlineWithPositioning={handleRegenerateHeadlineWithPositioning}
      positioningRegenerating={positioningRegenerating}
      peerPositionedHeadline={peerPositionedHeadline || data?.blueprint?.headline_peer_positioned || null}
      peerPositioning={peerPositioning || data?.peer_positioning || null}
    />
  );
  else if (pageKey === 'content.researched') {
    content = (
      <PostsResults
        posts={data.posts || []}
        voice={data.voice_profile}
        regenerating={regenerating || data.posts_status === 'regenerating'}
        postsStatus={data.posts_status}
        postsError={data.posts_error}
        analysisId={data.id}
        existingCalendar={data.calendar}
        latestPov={data.latest_pov}
        povHistory={data.pov_history}
        postContext={data.post_context}
        auditPlanContext={{
          presencePlan: audit.presencePlan,
          thirtyDayPlan: audit.thirtyDayPlan,
          selectedObjectiveLabel: audit.selectedObjectiveLabel,
          careerStage: audit.careerStage,
          targetAudienceOrOpportunity: audit.targetAudienceOrOpportunity,
        }}
        contentGated={contentGated}
        onUpgrade={onUpgrade}
        onRegenerateRequested={onRegenerateRequested}
        onRetryLast={onRetryLast}
      />
    );
  } else if (pageKey === 'content.pov') {
    content = (
      <PovPostsTab
        analysisId={analysisId}
        voice={data.voice_profile}
        latestPov={data.latest_pov}
        povHistory={data.pov_history}
        inspirationTopics={(data?.trending_topics || []).filter((t) => t && t.from_leader)}
        contentGated={contentGated}
        onUpgrade={onUpgrade}
        seedTheme={povSeedTheme}
        onSeedThemeConsumed={() => setPovSeedTheme(null)}
      />
    );
  } else if (pageKey === 'content.prompt') {
    content = (
      <PromptPostsTab
        analysisId={analysisId}
        voice={data.voice_profile}
        defaults={promptDefaults}
        contentGated={contentGated}
        onUpgrade={onUpgrade}
        positioningReport={data?.inspiration_positioning_report || null}
      />
    );
  } else if (pageKey === 'content.topics') {
    content = (
      <TopicsToWriteTab
        data={data}
        onDraftFromTopic={(label) => {
          setPromptDefaults({ topic: (label || '').slice(0, 240), custom_topics: [] });
          setPageKey('content.prompt');
          toast.success('Prompt Posts opened \u2014 topic pre-filled.');
        }}
      />
    );
  } else if (pageKey === 'content.peer_benchmark') {
    content = <PeerBenchmarkTab data={data} onUpgrade={onUpgrade} unlocked={peerUnlocked} />;
  } else if (pageKey === 'presence.keywords') content = <DiscoverabilityTab audit={audit} />;
  else if (pageKey === 'presence.themes') content = <PresenceTab audit={audit} onDraftFromTheme={handleDraftFromTheme} onDraftPovFromTheme={handleDraftPovFromTheme} />;
  else if (pageKey === 'presence.seven') content = <SevenDayPlanTab audit={audit} />;
  else if (pageKey === 'presence.thirty') content = <PlanTab audit={audit} />;

  return (
    <div data-testid="analysis-v2-layout">
      {/* Top nav strip — glass-morphism with gradient underline */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-6 rounded-2xl border border-white/60 dark:border-white/5 bg-white/60 dark:bg-slate-900/40 backdrop-blur-xl px-3 py-2 shadow-[0_4px_20px_-8px_rgba(120,90,200,0.15)]">
        {/* Desktop: 4 dropdowns; Mobile (<640px): single collapsed selector */}
        <nav className="hidden sm:flex flex-wrap items-center gap-1" data-testid="v2nav-root">
          {NAV.map((menu) => (
            <NavDropdown
              key={menu.id}
              menu={menu}
              activeKey={pageKey}
              onSelect={setPageKey}
              done={doneMap}
              gatedKeys={gated}
            />
          ))}
        </nav>
        <div className="sm:hidden flex-1 min-w-0" data-testid="v2nav-mobile">
          <MobileNavSelect
            activeKey={pageKey}
            onSelect={setPageKey}
            done={doneMap}
            gated={gated}
          />
        </div>

        {/* Download dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="rounded-full" data-testid="v2-download-menu">
              {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
              Download
              <ChevronDown size={12} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onDownload('pdf'); }}
              disabled={downloading === 'pdf'}
              data-testid="v2-download-pdf"
            >
              <FileDown size={13} className="mr-2" /> PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onDownload('docx'); }}
              disabled={downloading === 'docx'}
              data-testid="v2-download-docx"
            >
              <FileDown size={13} className="mr-2" /> DOCX
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Page content */}
      <div className="min-h-[400px]" data-testid="v2-page-content">
        {content}
      </div>
    </div>
  );
}
